import { createSlice, PayloadAction, current } from "@reduxjs/toolkit";
import { ReduxState } from "..";
import {
  createCustomFormat,
  deleteCustomFormat,
  getCustomFormats,
  updateCustomFormat,
} from "../../docuclipper/api";
import {
  CustomFormat,
  CustomFormatItem,
  CustomFormatSource,
  CustomFormatType,
} from "../../docuclipper/DocuclipperTypes";
import { createAlert } from "./Alerts";
import { lsKeySelectedCustomConfigCSV, lsKeySelectedExportType, setSelectedCustomConfigCSV, setSelectedExportType } from "./DownloadOptions";
import { setSelectedCustomFormatType } from "./DownloadInvoiceOptions";
import { lsSetItem, lsGetItem, lsRemoveItem } from "src/LocalStorageWrapper";

let uidCounter = 0;
const genUid = () =>
  `cfi-${Date.now().toString(36)}-${(uidCounter++).toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

// Guarantee every column has a stable `uid`. Backfills legacy configs saved
// before `uid` existed. Idempotent: existing uids are preserved.
const ensureColumnUids = (
  data: CustomFormat["data"]
): CustomFormat["data"] => ({
  ...data,
  columnConfigs: (data?.columnConfigs || []).map((c) => ({
    ...c,
    uid: c.uid || genUid(),
  })),
});

const defaultData: CustomFormat["data"] = {
  columnConfigs: [
    {
      index: 0,
      source: "date" as CustomFormatSource,
      params: {},
      type: "date",
      heading: { hasHeading: true, text: "Date" },
    },
    {
      index: 1,
      source: "name" as CustomFormatSource,
      params: {},
      type: "text",
      heading: { hasHeading: true, text: "Description" },
    },
    {
      index: 2,
      source: "amount" as CustomFormatSource,
      params: {},
      type: "number",
      heading: { hasHeading: true, text: "Amount" },
    },
  ],
};

const defaultInvoiceData: CustomFormat["data"] = {
  columnConfigs: [
    {
      index: 0,
      source: "id" as CustomFormatSource,
      params: {},
      type: "text",
      heading: { hasHeading: true, text: "Invoice Number" },
    },
    {
      index: 1,
      source: "date" as CustomFormatSource,
      params: {},
      type: "date",
      heading: { hasHeading: true, text: "Invoice Date" },
    },
    {
      index: 2,
      source: "dueDate" as CustomFormatSource,
      params: {},
      type: "date",
      heading: { hasHeading: true, text: "Invoice Due Date" },
    },
    {
      index: 3,
      source: "tax" as CustomFormatSource,
      params: {},
      type: "number",
      heading: { hasHeading: true, text: "Tax Amount" },
    },
    {
      index: 4,
      source: "total" as CustomFormatSource,
      params: {},
      type: "number",
      heading: { hasHeading: true, text: "Invoice Total" },
    },
    {
      index: 5,
      source: "item" as CustomFormatSource,
      params: {},
      type: "text",
      heading: { hasHeading: true, text: "Line Item Description" },
    },
    {
      index: 6,
      source: "quantity" as CustomFormatSource,
      params: {},
      type: "number",
      heading: { hasHeading: true, text: "Line Item Quantity" },
    },
    {
      index: 7,
      source: "unitPrice" as CustomFormatSource,
      params: {},
      type: "number",
      heading: { hasHeading: true, text: "Unit Price per Item" },
    },
    {
      index: 8,
      source: "price" as CustomFormatSource,
      params: {},
      type: "number",
      heading: { hasHeading: true, text: "Line Item Total" },
    },
    {
      index: 9,
      source: "service" as CustomFormatSource,
      params: {},
      type: "text",
      heading: { hasHeading: true, text: "Service Description" },
    },
    {
      index: 10,
      source: "category" as CustomFormatSource,
      params: {},
      type: "text",
      heading: { hasHeading: true, text: "Item/Service Category" },
    },
    {
      index: 11,
      source: "customer" as CustomFormatSource,
      params: {},
      type: "text",
      heading: { hasHeading: true, text: "Customer/Bill To" },
    },
    {
      index: 12,
      source: "vendor" as CustomFormatSource,
      params: {},
      type: "text",
      heading: { hasHeading: true, text: "Vendor/From" },
    },
    {
      index: 13,
      source: "poNumber" as CustomFormatSource,
      params: {},
      type: "text",
      heading: { hasHeading: true, text: "Purchase Order Number" },
    },
  ],
};

// Bank and invoice custom formats are stored in SEPARATE buckets. They used to
// share a single `customFormats` array + a `customFormatType` flag, which let a
// concurrent fetch from one flow clobber the other's list — making custom
// configs vanish from the export dropdown and reset to "All columns". Per-type
// storage removes that whole class of bug.
const initialState: {
  formatsByType: Record<CustomFormatType, CustomFormat[]>;
  loadingByType: Record<CustomFormatType, boolean>;
  // Which type the Configure Columns modal is currently editing. Not used for
  // dropdown option lists anymore (those read their own bucket directly).
  customFormatType: CustomFormatType;
  selected: null | CustomFormat;
} = {
  formatsByType: { bankStatement: [], invoice: [] },
  loadingByType: { bankStatement: false, invoice: false },
  selected: null,
  customFormatType: "bankStatement",
};

const slice = createSlice({
  name: "customFormat",
  initialState,
  reducers: {
    setFormatsForType(
      state,
      action: PayloadAction<{ type: CustomFormatType; formats: CustomFormat[] }>
    ) {
      const { type, formats } = action.payload;
      state.formatsByType[type] = formats;
      // Keep the selected reference fresh if it lives in this type's list.
      // Don't null it out otherwise — a background refetch of the *other* type
      // must not drop the modal's current selection.
      if (state.selected) {
        const match = formats.find(
          (x) => x.id === state.selected?.id || x.name === state.selected?.name
        );
        if (match) {
          state.selected = match;
        }
      }
    },
    setSelectedCustomFormat(state, action: PayloadAction<string>) {
      const cState = current(state);
      const all = [
        ...cState.formatsByType.bankStatement,
        ...cState.formatsByType.invoice,
      ];
      const selected = all.filter((x) => x.name === action.payload)[0];
      state.selected = selected || null;
    },
    setLoadingForType(
      state,
      action: PayloadAction<{ type: CustomFormatType; loading: boolean }>
    ) {
      state.loadingByType[action.payload.type] = action.payload.loading;
    },
    updateSelected(state, action: PayloadAction<CustomFormat | null>) {
      state.selected = action.payload;
    },
    setCustomFormatType(state, action: PayloadAction<CustomFormatType>) {
      state.customFormatType = action.payload;
    },
  },
});

export const {
  setFormatsForType,
  setSelectedCustomFormat,
  setLoadingForType,
  updateSelected,
  setCustomFormatType,
} = slice.actions;

// Resolve which type-bucket a format lives in (records carry no `type` field).
// Used by list mutations so they update the correct bucket regardless of which
// type the modal last had active.
const typeOfFormat = (
  state: ReduxState,
  format: CustomFormat
): CustomFormatType => {
  if (
    state.CustomFormat.formatsByType.bankStatement.some(
      (x) => x.id === format.id
    )
  ) {
    return "bankStatement";
  }
  if (
    state.CustomFormat.formatsByType.invoice.some((x) => x.id === format.id)
  ) {
    return "invoice";
  }
  return state.CustomFormat.customFormatType;
};


export const createCustomFormatRedux = ({
  type,
  callback = (newFormatName = "") => { },
}: {
  type: CustomFormatType;
  callback?: (newFormatName) => void;
}) => async (dispatch, getState) => {
  const state: ReduxState = getState();
  try {
    const existingNames = state.CustomFormat.formatsByType[type].map(
      (x) => x.name
    );
    let nameIndex = 1;
    let name = `configuration ${nameIndex}`;

    while (existingNames.includes(name)) {
      nameIndex += 1;
      name = `configuration ${nameIndex}`;
    }

    const cf = await createCustomFormat(
      name,
      type === "invoice" ? defaultInvoiceData : defaultData,
      type
    );
    try {
      cf.data = JSON.parse(cf.data);
    } catch (err) {
      cf.data = { columnConfigs: [] };
    }
    cf.data = ensureColumnUids(cf.data);

    dispatch(
      setFormatsForType({
        type,
        formats: [...state.CustomFormat.formatsByType[type], cf],
      })
    );
    dispatch(setCustomFormatType(type));
    dispatch(setSelectedCustomFormat(cf.name));

    const newCustomFormatName = `custom-${cf.name}` as any;

    if (type === "bankStatement") {
      dispatch(setSelectedExportType(newCustomFormatName));
      dispatch(setSelectedCustomConfigCSV({
        value: newCustomFormatName,
        label: cf.name.charAt(0).toUpperCase() + cf.name.slice(1)
      }));

      lsSetItem(lsKeySelectedExportType, JSON.stringify({
        value: newCustomFormatName,
      }));
      lsSetItem(lsKeySelectedCustomConfigCSV, JSON.stringify({
        value: newCustomFormatName,
        label: cf.name.charAt(0).toUpperCase() + cf.name.slice(1),
      }));
    } else if (type === "invoice") {
      // Mirror the bank branch for invoices: make the just-created config the
      // selected output format. Without this, creating from the modal's
      // "Add new configuration" button leaves the invoice output dropdown on
      // "All columns" (custom-all). The reducer persists this to localStorage.
      dispatch(setSelectedCustomFormatType(newCustomFormatName));
    }

    callback(newCustomFormatName);
  } catch (err) {
    //
  }
};

export const deleteSelectedCustomFormat = () => async (dispatch, getState) => {
  const state: ReduxState = getState();
  if (!state.CustomFormat.selected) {
    return;
  }
  if (!/^\d+$/.test(String(state.CustomFormat.selected.id))) {
    dispatch(updateSelected(null));
    return;
  }
  try {
    const deletedFormatName = state.CustomFormat.selected.name;
    const deletedFormatValue = `custom-${deletedFormatName}`;
    
    // Read localStorage values before any modifications
    const currentExportType = JSON.parse(lsGetItem(lsKeySelectedExportType) || '{}').value;
    const currentCustomConfigCSV = JSON.parse(lsGetItem(lsKeySelectedCustomConfigCSV) || '{}');
    
    const needsCleanup = 
      currentExportType === deletedFormatValue || 
      currentCustomConfigCSV.value === deletedFormatValue;
    
    const formatType = typeOfFormat(state, state.CustomFormat.selected);
    const newFormats = state.CustomFormat.formatsByType[formatType].filter(
      (x) => x.name !== deletedFormatName
    );
    dispatch(setFormatsForType({ type: formatType, formats: newFormats }));
    await deleteCustomFormat(state.CustomFormat.selected.id);

    // Clean up localStorage and switch to "custom-all" if the deleted format was selected
    if (needsCleanup) {
      // Switch to "custom-all" (all columns) as default
      dispatch(setSelectedExportType("custom-all" as any));
      dispatch(setSelectedCustomConfigCSV({
        value: "custom-all",
        label: "All (all columns)"
      }));
    }

    dispatch(
      setSelectedCustomFormat(newFormats.length > 0 ? newFormats[0].name : "")
    );
    if (newFormats.length === 0) {
      dispatch(updateSelected(null));
    }
  } catch (err) {
    //
  }
};

export const deleteCustomFormatItemRedux =
  (cfi: CustomFormatItem) => async (dispatch, getState) => {
    const state: ReduxState = getState();
    if (!state.CustomFormat.selected) {
      return;
    }
    if (!/^\d+$/.test(String(state.CustomFormat.selected.id))) {
      dispatch(updateSelected(null));
      return;
    }

    const newSelected = {
      ...state.CustomFormat.selected,
      data: {
        ...state.CustomFormat.selected.data,
        columnConfigs: state.CustomFormat.selected.data.columnConfigs
          .filter((x) => x.index !== cfi.index)
          .map((x, i) => ({ ...x, index: i })),
      },
    };

    try {
      await updateCustomFormat(newSelected);
      dispatch(updateSelected(newSelected));
    } catch (err) {
      //
      dispatch(
        createAlert({
          message: "Error updating configuration. Please try again.",
          timeout: 0,
          type: "error",
        })
      );
    }
  };

export const addCustomFormatItemRedux = () => async (dispatch, getState) => {
  const state: ReduxState = getState();
  if (!state.CustomFormat.selected) {
    return;
  }
  if (!/^\d+$/.test(String(state.CustomFormat.selected.id))) {
    dispatch(updateSelected(null));
    return;
  }

  const newCfi: CustomFormatItem = {
    index: state.CustomFormat.selected.data.columnConfigs.length,
    uid: genUid(),
    params: {},
    type: "date",
    source: "date" as CustomFormatSource,
  };
  const newSelected = {
    ...state.CustomFormat.selected,
    data: {
      ...state.CustomFormat.selected.data,
      columnConfigs: [
        ...state.CustomFormat.selected.data.columnConfigs,
        newCfi,
      ],
    },
  };

  try {
    await updateCustomFormat(newSelected);
    dispatch(updateSelected(newSelected));
  } catch (err) {
    //
    dispatch(
      createAlert({
        message: "Error updating configuration. Please try again.",
        timeout: 0,
        type: "error",
      })
    );
  }
};

export const reorderCustomFormatItemsRedux =
  ({ startIndex, endIndex }: { startIndex: number; endIndex: number }) =>
  async (dispatch, getState) => {
    const state: ReduxState = getState();
    if (!state.CustomFormat.selected) {
      return;
    }
    if (!/^\d+$/.test(String(state.CustomFormat.selected.id))) {
      dispatch(updateSelected(null));
      return;
    }
    if (startIndex === endIndex) {
      return;
    }

    const reordered = [...state.CustomFormat.selected.data.columnConfigs];
    const [moved] = reordered.splice(startIndex, 1);
    reordered.splice(endIndex, 0, moved);

    const newSelected = {
      ...state.CustomFormat.selected,
      data: {
        ...state.CustomFormat.selected.data,
        columnConfigs: reordered.map((x, i) => ({ ...x, index: i })),
      },
    };

    // Optimistically reflect the new order before the API round-trip so the
    // drag feels instant; revert on failure.
    const previousSelected = state.CustomFormat.selected;
    dispatch(updateSelected(newSelected));

    try {
      await updateCustomFormat(newSelected);
    } catch (err) {
      dispatch(updateSelected(previousSelected));
      dispatch(
        createAlert({
          message: "Error updating configuration. Please try again.",
          timeout: 0,
          type: "error",
        })
      );
    }
  };

export const updateCustomFormatName =
  (name: string) => async (dispatch, getState) => {
    const state: ReduxState = getState();
    if (!state.CustomFormat.selected) {
      return;
    }
    if (!/^\d+$/.test(String(state.CustomFormat.selected.id))) {
      dispatch(updateSelected(null));
      return;
    }

    const newSelected = {
      ...state.CustomFormat.selected,
      name,
    };

    const formatType = typeOfFormat(state, state.CustomFormat.selected);
    const newFormats = state.CustomFormat.formatsByType[formatType].map((x) =>
      x.name === state.CustomFormat.selected?.name ? { ...x, name } : x
    );
    try {
      await updateCustomFormat(newSelected);
      dispatch(updateSelected(newSelected));
      dispatch(setFormatsForType({ type: formatType, formats: newFormats }));
      dispatch(setSelectedCustomFormat(newSelected.name));
      lsSetItem(lsKeySelectedCustomConfigCSV, JSON.stringify({
        value: `custom-${newSelected.name}`,
        label: newSelected.name.charAt(0).toUpperCase() + newSelected.name.slice(1),
      }))
    } catch (err) {
      dispatch(
        createAlert({
          message: "Error updating configuration. Please try again.",
          timeout: 0,
          type: "error",
        })
      );
    }
  };

export const updateCustomFormatItemRedux =
  (cfi: CustomFormatItem) => async (dispatch, getState) => {
    const state: ReduxState = getState();
    if (!state.CustomFormat.selected) {
      return;
    }
    if (!/^\d+$/.test(String(state.CustomFormat.selected.id))) {
      dispatch(updateSelected(null));
      return;
    }
    if (!state.CustomFormat.selected) {
      return;
    }

    const newSelected = {
      ...state.CustomFormat.selected,
      data: {
        ...state.CustomFormat.selected.data,
        columnConfigs: state.CustomFormat.selected.data.columnConfigs.map((x) =>
          x.index === cfi.index ? { ...x, ...cfi } : x
        ),
      },
    };

    try {
      await updateCustomFormat(newSelected);
      dispatch(updateSelected(newSelected));
    } catch (err) {
      dispatch(
        createAlert({
          message: "Error updating configuration. Please try again.",
          timeout: 0,
          type: "error",
        })
      );
    }
  };

type CustomFormatsProps = {
  init: boolean;
  type: 'bankStatement' | 'invoice'
}

export const fetchCustomFormats = ({
  init,
  type
}: CustomFormatsProps) => async (dispatch, getState) => {
  const state: ReduxState = getState();
  // Per-type loading guard: a bank fetch must not be dropped because an invoice
  // fetch is in flight (and vice versa).
  if (state.CustomFormat.loadingByType[type]) {
    return;
  }

  try {
    dispatch(setLoadingForType({ type, loading: true }));
    const customFormats = await getCustomFormats({
      type,
    });
    dispatch(
      setFormatsForType({
        type,
        formats: customFormats.map((x) => {
          let data = { columnConfigs: [] };
          try {
            data = JSON.parse(x.data);
          } catch (err) {
            //
            console.error(err);
          }
          return { ...x, data: ensureColumnUids(data) };
        }),
      })
    );
    dispatch(setLoadingForType({ type, loading: false }));
    // Auto-select the first format only for the modal's initial load. Don't
    // flip `customFormatType` here — background dropdown fetches must not change
    // which type the modal is editing (the modal sets that itself).
    if (init) {
      dispatch(setCustomFormatType(type));
      if (customFormats.length > 0 && !state.CustomFormat.selected) {
        const cf = customFormats[0];
        dispatch(setSelectedCustomFormat(cf.name));
        dispatch(setSelectedExportType(`custom-${cf.name}` as any));
      }
    }
  } catch (err) {
    dispatch(setLoadingForType({ type, loading: false }));
    //
  }
};

export default slice.reducer;
